// src/services/reminderApi.js
// Servicio para manejar operaciones relacionadas con recordatorios

import { dotoniwoofApi } from '../api/dotoniwooApi';

/**
 * Obtiene la lista de recordatorios con filtros
 * @param {Object} filters - Filtros de búsqueda (opcional)
 * @returns {Promise<Object>} Lista de recordatorios
 */
export const getReminders = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Agregar filtros si están definidos
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    
    const queryString = params.toString();
    const url = queryString ? `/reminder.php?${queryString}` : '/reminder.php';
    const response = await dotoniwoofApi.get(url);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener recordatorios:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene un recordatorio por su ID
 * @param {number} reminderId - ID del recordatorio
 * @returns {Promise<Object>} Datos del recordatorio
 */
export const getReminderById = async (reminderId) => {
  try {
    const response = await dotoniwoofApi.get(`/reminder.php/${reminderId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener recordatorio:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene el recordatorio actual de un cliente
 * @param {number} clientId - ID del cliente
 * @returns {Promise<Object>} Recordatorio actual del cliente
 */
export const getCurrentReminderByClient = async (clientId) => {
  try {
    const response = await dotoniwoofApi.get(`/reminder.php?action=current-by-client&client_id=${clientId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener recordatorio actual:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene el historial de recordatorios de un cliente
 * @param {number} clientId - ID del cliente
 * @returns {Promise<Object>} Historial de recordatorios
 */
export const getReminderHistoryByClient = async (clientId) => {
  try {
    const response = await dotoniwoofApi.get(`/reminder.php?action=history-by-client&client_id=${clientId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener historial de recordatorios:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene el historial detallado de cambios de un recordatorio
 * @param {number} reminderId - ID del recordatorio
 * @returns {Promise<Object>} Historial detallado
 */
export const getReminderHistoryDetails = async (reminderId) => {
  try {
    const response = await dotoniwoofApi.get(`/reminder.php/${reminderId}?action=history-details`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener historial detallado:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Crea un nuevo recordatorio
 * @param {Object} reminderData - Datos del recordatorio
 * @param {number} reminderData.id_client - ID del cliente
 * @param {number} reminderData.id_status - ID del estado (1 = PENDIENTE)
 * @param {string} reminderData.reminder_date - Fecha del recordatorio (YYYY-MM-DD HH:mm:ss)
 * @param {string} [reminderData.notes] - Notas adicionales (opcional)
 * @returns {Promise<Object>} Respuesta de la API
 */
export const createReminder = async (reminderData) => {
  try {
    console.log('Enviando datos del recordatorio:', reminderData);
    
    const response = await dotoniwoofApi.post('/reminder.php', reminderData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al crear recordatorio:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Actualiza un recordatorio existente
 * @param {number} reminderId - ID del recordatorio
 * @param {Object} reminderData - Nuevos datos del recordatorio
 * @returns {Promise<Object>} Respuesta de la API
 */
export const updateReminder = async (reminderId, reminderData) => {
  try {
    console.log('Actualizando recordatorio:', reminderId, reminderData);
    
    const response = await dotoniwoofApi.put(`/reminder.php/${reminderId}`, reminderData);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al actualizar recordatorio:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Marca un recordatorio como completado (cuando el cliente agendó)
 * @param {number} reminderId - ID del recordatorio
 * @param {Object} data - Datos adicionales
 * @param {number} data.id_appointment - ID de la cita agendada
 * @param {number} data.changed_by_person - ID de la persona que realiza el cambio
 * @returns {Promise<Object>} Respuesta de la API
 */
export const markReminderAsCompleted = async (reminderId, data) => {
  try {
    console.log('Marcando recordatorio como completado:', reminderId, data);
    
    const response = await dotoniwoofApi.post(`/reminder.php/${reminderId}?action=mark-completed`, data);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al marcar recordatorio como completado:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Marca un recordatorio como cancelado (cuando el cliente desiste)
 * @param {number} reminderId - ID del recordatorio
 * @param {Object} data - Datos adicionales
 * @param {string} [data.notes] - Notas sobre la cancelación
 * @param {number} data.changed_by_person - ID de la persona que realiza el cambio
 * @returns {Promise<Object>} Respuesta de la API
 */
export const markReminderAsCancelled = async (reminderId, data) => {
  try {
    console.log('Marcando recordatorio como cancelado:', reminderId, data);
    
    const response = await dotoniwoofApi.post(`/reminder.php/${reminderId}?action=mark-cancelled`, data);
    
    console.log('Respuesta del servidor:', response.data);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al marcar recordatorio como cancelado:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Elimina un recordatorio
 * @param {number} reminderId - ID del recordatorio
 * @returns {Promise<Object>} Respuesta de la API
 */
export const deleteReminder = async (reminderId) => {
  try {
    const response = await dotoniwoofApi.delete(`/reminder.php/${reminderId}`);
    
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al eliminar recordatorio:', error);
    
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};
